import { createContext, useContext, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useCampaign } from "./context";
import { Markdown } from "./Markdown";
import { AttachmentButton, CampaignError, fieldText, useAction, useEntries, useEntry } from "./parts";
import { RevealButton, useSituation, type RevealTarget } from "./play";
import type { CampaignEntry, MaterialReference, SituationMaterial } from "./types";

function sameReference(a: MaterialReference, b: MaterialReference): boolean {
  return a.entryId === b.entryId && (a.sectionId ?? null) === (b.sectionId ?? null) && (a.field ?? null) === (b.field ?? null) && (a.attachmentId ?? null) === (b.attachmentId ?? null);
}

/** The references of the field material of a scene, as the view sees them. */
export function materialOf(scene: Pick<CampaignEntry, "fields"> | undefined): MaterialReference[] {
  const value = scene?.fields.find((f) => f.key === "material")?.value;
  return Array.isArray(value) ? (value as MaterialReference[]) : [];
}

interface MaterialContextValue {
  sceneTitle: string;
  has: (reference: MaterialReference) => boolean;
  toggle: (reference: MaterialReference) => Promise<boolean>;
}

const MaterialContext = createContext<MaterialContextValue | null>(null);

/**
 * In the dossier an entry's parts can be put together as material of a scene: the active one, or another chosen here. The field
 * material is secret, so only the dossier prepares it.
 */
export function MaterialProvider({ entry, off = false, children }: { entry: CampaignEntry; off?: boolean; children: ReactNode }) {
  const campaign = useCampaign();
  const scenes = useEntries({ kind: "scene" });
  const [chosen, setChosen] = useState<string | null>(null);
  const available = !off && campaign.mode === "dossier" && entry.kind !== "scene" && entry.kind !== "campaign" && (scenes.data ?? []).length > 0;
  const sceneId = available ? (chosen ?? (scenes.data?.some((s) => s.id === campaign.head.play.sceneId) ? campaign.head.play.sceneId : scenes.data?.[0]?.id) ?? null) : null;
  const scene = useEntry(sceneId);
  const action = useAction();
  if (!available || !sceneId) return <>{children}</>;
  const references = materialOf(scene.data);
  const value: MaterialContextValue = {
    sceneTitle: scene.data?.title ?? "",
    has: (reference) => references.some((r) => sameReference(r, reference)),
    toggle: (reference) =>
      action.run(() =>
        campaign.apply({
          type: "campaign.scene.material",
          payload: { id: sceneId, action: references.some((r) => sameReference(r, reference)) ? "remove" : "add", ...reference },
        }),
      ),
  };
  const term = campaign.term("scene");
  return (
    <MaterialContext.Provider value={value}>
      <div className="card material-bar">
        <label className="field">
          <span>Material für ({term})</span>
          <select value={sceneId} onChange={(e) => setChosen(e.target.value)}>
            {scenes.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
                {s.id === campaign.head.play.sceneId ? " (aktiv)" : ""}
              </option>
            ))}
          </select>
        </label>
        <p className="muted small">
          Ohne Dossier ist Material sichtbar, solange der gewählte Eintrag ({term}) aktiv ist, auch wenn es geheim ist. {campaign.term("player")} sehen es nie.
        </p>
        <MaterialToggle reference={{ entryId: entry.id }} label="Ganzer Eintrag" whole />
        <CampaignError error={action.error} onClose={action.clear} />
      </div>
      {children}
    </MaterialContext.Provider>
  );
}

/** Adds a part of the entry to the material of the chosen scene or removes it; only within {@link MaterialProvider}. */
export function MaterialToggle({ reference, label, whole = false }: { reference: MaterialReference; label: string; whole?: boolean }) {
  const context = useContext(MaterialContext);
  if (!context) return null;
  const on = context.has(reference);
  return (
    <button
      type="button"
      className={`chip small material-toggle${on ? " selected" : ""}`}
      aria-pressed={on}
      aria-label={`${label}: Material für „${context.sceneTitle}“`}
      onClick={() => void context.toggle(reference)}
    >
      {whole && "Ganzer Eintrag: "}
      {on ? "Material ✓" : "+ Material"}
    </button>
  );
}

/** What revealing a part of the material reveals. */
export function revealOf(item: SituationMaterial): RevealTarget {
  const base = { entryTitle: item.title, seenByParts: item.seenByParts };
  if (item.sectionId) return { ...base, target: "section", id: item.sectionId, label: `Abschnitt „${item.heading ?? ""}“` };
  if (item.attachmentId) return { ...base, target: "attachment", id: item.attachmentId, label: `Anhang „${item.attachmentName ?? ""}“` };
  if (item.field) return { ...base, target: "field", id: item.entryId, field: item.field, label: `Feld „${item.fieldLabel?.de ?? item.field}“` };
  return { ...base, target: "entry", id: item.entryId, label: `„${item.title}“` };
}

function partLabel(item: SituationMaterial): string | null {
  if (item.sectionId) return item.heading ?? null;
  if (item.attachmentId) return item.attachmentName ?? null;
  if (item.field) return item.fieldLabel?.de ?? item.field;
  return null;
}

/** The material of the active scene with what the game master needs at the table; secret parts are revealed with one tap. */
export function MaterialList({ material, sceneId }: { material: SituationMaterial[]; sceneId: string }) {
  const campaign = useCampaign();
  const action = useAction();
  if (material.length === 0) return <p className="muted small">Kein Material.</p>;
  return (
    <ul className="plain-list material-list" aria-label="Szenenmaterial">
      {material.map((item, index) => {
        const part = partLabel(item);
        const secret = item.visibility === "secret" || !!item.seenByParts;
        const reference: MaterialReference = { entryId: item.entryId };
        if (item.sectionId) reference.sectionId = item.sectionId;
        if (item.field) reference.field = item.field;
        if (item.attachmentId) reference.attachmentId = item.attachmentId;
        return (
          <li key={index} className={`material${secret ? " secret" : ""}`} aria-label={part ? `${item.title}: ${part}` : item.title}>
            <div className="row">
              <span className="grow">
                <Link to={campaign.entryPath(item.entryId)}>{item.title}</Link>
                {part && <span className="muted"> · {part}</span>}
              </span>
              {secret && <span className="badge secret">{campaign.mode === "dossier" ? "geheim" : "Szenenmaterial"}</span>}
              {secret && <RevealButton reveal={revealOf(item)} />}
              {campaign.mode === "dossier" && (
                <button
                  type="button"
                  className="link danger"
                  onClick={() => void action.run(() => campaign.apply({ type: "campaign.scene.material", payload: { id: sceneId, action: "remove", ...reference } }))}
                >
                  Entfernen
                </button>
              )}
            </div>
            {item.text?.trim() && <Markdown text={item.text} references={[]} entryPath={campaign.entryPath} />}
            {item.field && item.value != null && <p className="pre-line">{fieldText(item.value)}</p>}
            {item.attachmentId && <AttachmentButton attachment={{ id: item.attachmentId, name: item.attachmentName ?? "" }} />}
          </li>
        );
      })}
      <CampaignError error={action.error} onClose={action.clear} />
    </ul>
  );
}

/** The material on the page of a scene: while it is active as at the table, else in the dossier as the list it prepares. */
export function SceneMaterial({ scene }: { scene: CampaignEntry }) {
  const campaign = useCampaign();
  const active = campaign.head.play.sceneId === scene.id;
  const situation = useSituation();
  const prepared = materialOf(scene);
  if (!active && campaign.mode !== "dossier") return null;
  return (
    <section className="card" aria-label="Material">
      <h2>Material</h2>
      {active ? (
        situation.error ? (
          <CampaignError error={situation.error} reload />
        ) : situation.data?.scene ? (
          <MaterialList material={situation.data.scene.material} sceneId={scene.id} />
        ) : (
          <p className="muted small">Lade …</p>
        )
      ) : prepared.length === 0 ? (
        <p className="muted small">Noch kein Material. Material stellst du im Dossier auf den Seiten der Einträge zusammen.</p>
      ) : (
        <ul className="plain-list" aria-label="Szenenmaterial">
          {prepared.map((reference, index) => (
            <PreparedItem key={index} sceneId={scene.id} reference={reference} />
          ))}
        </ul>
      )}
      {campaign.mode === "dossier" && (
        <p className="muted small">Material stellst du auf den Seiten der Einträge zusammen: „+ Material“ am Eintrag, an Abschnitten, Feldern und Anhängen.</p>
      )}
    </section>
  );
}

function PreparedItem({ sceneId, reference }: { sceneId: string; reference: MaterialReference }) {
  const campaign = useCampaign();
  const entry = useEntry(reference.entryId);
  const action = useAction();
  const data = entry.data;
  const part = reference.sectionId
    ? `Abschnitt „${data?.sections.find((s) => s.id === reference.sectionId)?.heading ?? "…"}“`
    : reference.attachmentId
      ? `Anhang „${data?.attachments.find((a) => a.id === reference.attachmentId)?.name ?? "…"}“`
      : reference.field
        ? `Feld „${campaign.kind(data?.kind ?? "")?.fields.find((f) => f.key === reference.field)?.label.de ?? reference.field}“`
        : null;
  return (
    <li className="row">
      <span className="grow">
        <Link to={campaign.entryPath(reference.entryId)}>{data?.title ?? "…"}</Link>
        {part && <span className="muted"> · {part}</span>}
      </span>
      <button
        type="button"
        className="link danger"
        onClick={() => void action.run(() => campaign.apply({ type: "campaign.scene.material", payload: { id: sceneId, action: "remove", ...reference } }))}
      >
        Entfernen
      </button>
      <CampaignError error={action.error} onClose={action.clear} />
    </li>
  );
}
